import React from 'react';
import { Box, Container, Typography, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { LpHeader, LpFooter, IsThisYou, LpCtaBlock, useScrollFade, fadeStyle, useLpTracking } from './LpShared';
import { trackEvent } from '../analytics';

const PAGE = '/lp/insurance';

export default function LpInsurance() {
  const navigate = useNavigate();
  useLpTracking(PAGE);
  const costSection = useScrollFade();
  const solveSection = useScrollFade();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <LpHeader ctaLabel="Partner with SMCLA" ctaPath="/contact" pagePath={PAGE} />

      {/* ── Hero ── */}
      <Box sx={{ position: 'relative', minHeight: { xs: 520, md: 680 }, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <Box component="img" src="/ai-tech.png" alt="AI risk analysis" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.15 }} />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(10,10,18,0.97) 0%, rgba(10,10,18,0.72) 55%, rgba(10,10,18,0.9) 100%)' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, py: { xs: 8, md: 12 }, px: 3 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,159,67,0.1)', border: '1px solid rgba(255,159,67,0.3)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ff9f43' }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#ff9f43', letterSpacing: '0.1em', textTransform: 'uppercase' }}>For Insurance Partners</Typography>
          </Box>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.3rem', md: '3.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', lineHeight: 1.05, mb: 3 }}>
            You can't price risk<br />
            <Box component="span" sx={{ color: '#ff9f43' }}>you can't see. Now you can.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 20 }, color: '#9499b0', maxWidth: 580, lineHeight: 1.65, mb: 5 }}>
            SMCLA-monitored facilities have something no other daycare book has: verified, real-time behavioral data. For the first time, you can differentiate genuinely safer facilities — and price them accordingly.
          </Typography>
          <Box>
            <Box
              component="button"
              onClick={() => { trackEvent('cta_click', 'Partner with SMCLA', PAGE, { location: 'lp_hero' }); navigate('/contact'); }}
              sx={{ display: 'inline-block', px: 4, py: 1.5, fontWeight: 700, fontSize: 16, fontFamily: 'Inter, system-ui, sans-serif', bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, border: 'none', cursor: 'pointer', boxShadow: '0 0 24px rgba(92,225,230,0.3)', '&:hover': { bgcolor: '#4ecfd4' } }}
            >
              Request an Insurance Partner Briefing →
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Is this you? ── */}
      <Box sx={{ py: { xs: 7, md: 10 }, px: 3 }}>
        <Container maxWidth="md">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <IsThisYou items={[
                "Your daycare commercial book has been hit harder than projected three years running",
                "You're raising premiums — but you can't objectively differentiate safe facilities from unsafe ones",
                "You want a verified monitoring discount tier but have no way to verify actual monitoring",
                "One $16M payout wiped out a decade of premiums from that account",
              ]} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box component="img" src="/country-daycare.png" alt="Daycare facility" sx={{ width: '100%', borderRadius: 3, border: '1px solid rgba(255,255,255,0.07)', display: 'block', opacity: 0.85 }} />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── The exposure ── */}
      <Box ref={costSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 7, md: 10 }, px: 3, ...fadeStyle(costSection.inView) }}>
        <Container maxWidth="md">
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#ff9f43', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5 }}>The book-level exposure</Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 5 }}>
            The daycare liability landscape keeps getting worse.
          </Typography>
          <Grid container spacing={3}>
            {[
              { num: '$428B', label: 'CDC-estimated lifetime economic burden of child maltreatment', sub: 'New cases per year, US only. Healthcare, justice, and productivity losses. (CDC, 2015)', color: '#ff6b6b' },
              { num: '$262K', label: 'Average physical abuse case settlement', sub: 'North Carolina, 2024. And that\'s without a fatality claim in the case.', color: '#ff9f43' },
              { num: '21.9%', label: 'Of daycare maltreatment cases involve sexual abuse', sub: 'Dramatically higher than in other supervised settings. (NCBI, Maltreatment in Daycare Settings)', color: '#8c52ff' },
            ].map(({ num, label, sub, color }) => (
              <Grid key={num} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: `1px solid ${color}20`, borderRadius: 3, height: '100%' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: '2rem', color, lineHeight: 1, mb: 1 }}>{num}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 13, color: '#f0f0f8', mb: 0.75, lineHeight: 1.4 }}>{label}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: '#9499b0', lineHeight: 1.6, fontStyle: 'italic' }}>{sub}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── How SMCLA solves it ── */}
      <Box ref={solveSection.ref} sx={{ py: { xs: 7, md: 10 }, px: 3, ...fadeStyle(solveSection.inView) }}>
        <Container maxWidth="md">
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5 }}>The actuarial proposition</Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 5 }}>
            Verified monitoring changes what you can charge — and what you pay out.
          </Typography>
          <Stack spacing={2.5}>
            {[
              { num: '1', title: 'Justify an actuarially defensible discount tier', body: 'SMCLA-monitored facilities carry verified behavioral detection data — the kind that correlates with lower claims frequency. You can offer a discount tier you can defend to your actuaries and your regulator.' },
              { num: '2', title: 'Fewer incidents reach claim severity', body: 'Real-time behavioral detection intervenes before incidents escalate. The event that would have become a $262K claim is caught at the alert stage — reviewed, addressed, and documented without a child being harmed.' },
              { num: '3', title: 'Forensic evidence shortens disputes and litigation', body: 'Every SMCLA incident comes with tamper-evident, chain-of-custody footage. Disputed claims resolve faster. He-said-she-said cases with no evidence become claims with clear facts. Legal costs drop materially.' },
            ].map(({ num, title, body }) => (
              <Box key={num} sx={{ display: 'flex', gap: 3, p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3, alignItems: 'flex-start', '&:hover': { borderColor: 'rgba(92,225,230,0.25)' }, transition: 'border-color 0.2s' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 16, color: '#5ce1e6' }}>{num}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#f0f0f8', mb: 1 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      <LpCtaBlock
        headline="A better daycare book starts here."
        sub="Book a briefing with our insurance partnerships team. We'll walk you through the risk model, discount tier structure, and revenue share program."
        ctaLabel="Request an Insurance Partner Briefing"
        ctaPath="/contact"
        pagePath={PAGE}
      />
      <LpFooter />
    </Box>
  );
}
